const _excluded = ["accumulatedCost", "row"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Decimal from 'decimal.js';
import { uniqBy, xor } from 'lodash-es';
import { convertStrToRichText } from 'seagull';
import { isBoolField, isBoolTrue, createErrorObject, parseCsv, validateCsvColumns, addCsvLineNumbers, validateCsvRow } from "../../components/ImportWizard/ImportWizard.utils";
import { decimalFromStr } from "../../infra/utils";
import { serviceTemplateModel } from "../../models";
import { ServiceCostType } from "../../models/service-cost/model";
import { BillingTriggerType } from "../../types/api.model";
import { extractBundles, validateBundleColumnsConsistency, validateBundleServicesSize } from "./bundlesValidation.utils";
import { discountTypeOptions, errorMessages, getBillingOccurrenceValidations, getPricingValidations, getServiceNameValidations, bundleColumnsToValidate, getBundlePricingType, getServiceCost } from "./servicesValidation.utils";
export const getServicesCsvColumnConfig = options => _objectSpread({
  ['service name']: _objectSpread(_objectSpread({
    inputName: 'serviceName',
    required: true
  }, getServiceNameValidations()), {}, {
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getEmptyStringError(headerName))
  }),
  ['service description']: {
    inputName: 'serviceDescription'
  },
  ['automatic billing']: {
    inputName: 'automaticBilling',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
    validate: isBoolField,
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName))
  },
  ['billing occurrence']: _objectSpread({
    inputName: 'billingOccurrence'
  }, getBillingOccurrenceValidations({
    excludeValues: ['in stages']
  })),
  ['pricing']: _objectSpread({
    inputName: 'pricing'
  }, getPricingValidations()),
  ['rate']: {
    inputName: 'rate',
    required: true,
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.priceError),
    validate: (price, _ref) => {
      let {
        rowObject
      } = _ref;
      const isRangePricing = rowObject.pricing.toLowerCase() === 'range';
      if (price.startsWith('-')) {
        return {
          errorType: isRangePricing ? 'rangePriceError' : 'priceError'
        };
      }
      // split in case of range price format ${min}-${max}
      const priceSplit = price.split('-');
      const trimmedPrices = priceSplit.map(p => p.trim());
      const hasMoreThanOnePrice = priceSplit.length > 1 && priceSplit[0].length && priceSplit[1].length;
      if (hasMoreThanOnePrice) {
        if (!isRangePricing) return {
          errorType: 'incompatiblePriceAndPricing'
        };
        const [first, second] = trimmedPrices;
        const min = decimalFromStr(first);
        const max = decimalFromStr(second);
        if (min && max && min.gt(max)) return {
          errorType: 'minPriceExceedMaxPrice'
        };
      }
      const validPrices = trimmedPrices.every(p => {
        const decimal = decimalFromStr(p);
        return !!decimal && decimal.isPositive();
      });
      return validPrices || {
        errorType: isRangePricing ? 'rangePriceError' : 'priceError'
      };
    },
    validateError: (headerName, rowNumber, errorType) => {
      switch (errorType) {
        case 'priceError':
          return createErrorObject(headerName, rowNumber, errorMessages.priceError);
        case 'rangePriceError':
          return createErrorObject(headerName, rowNumber, errorMessages.rangePriceError);
        case 'incompatiblePriceAndPricing':
          return createErrorObject(headerName, rowNumber, errorMessages.incompatiblePriceAndPricingMsg);
        case 'minPriceExceedMaxPrice':
          return createErrorObject(headerName, rowNumber, errorMessages.minPriceExceedingError);
      }
    }
  },
  ['unit cap']: {
    inputName: 'unitCap',
    validate: unitCap => {
      if ((unitCap === null || unitCap === void 0 ? void 0 : unitCap.length) === 0) return true;
      const decimal = decimalFromStr(unitCap);
      if (!decimal) return false;
      const isPositiveNumber = decimal.isPositive();
      const isNonZero = !decimal.isZero();
      return isPositiveNumber && isNonZero;
    },
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.positiveError)
  },
  ['unit name']: {
    inputName: 'unitName',
    required: true,
    validate: (unitName, _ref2) => {
      let {
        rowObject
      } = _ref2;
      const isQuantityPricing = rowObject.pricing.toLowerCase() === 'quantity';
      return !(isQuantityPricing && !unitName);
    },
    validateError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.getEmptyStringError(headerName))
  },
  ['discount']: {
    inputName: 'discount',
    validate: (discount, _ref3) => {
      var _rowObject$discountTy;
      let {
        rowObject
      } = _ref3;
      if ((discount === null || discount === void 0 ? void 0 : discount.length) === 0) return true;
      const decimal = decimalFromStr(discount);
      if (!decimal) return {
        errorType: 'negative'
      };
      const isPositiveNumber = decimal.isPositive();
      if (!isPositiveNumber) {
        return {
          errorType: 'negative'
        };
      }
      const discountType = ((_rowObject$discountTy = rowObject.discountType) === null || _rowObject$discountTy === void 0 ? void 0 : _rowObject$discountTy.toLowerCase()) || '';
      if (discountType === 'percent' && decimal.gt(100)) {
        return {
          errorType: 'percentLargerThan100'
        };
      }
      if (discountType === 'flat' && rowObject.pricing.toLowerCase() !== 'range' && decimal.gt(rowObject.rate)) {
        return {
          errorType: 'flatLargerThanPrice'
        };
      }
      return true;
    },
    validateError: (headerName, rowNumber, errorType) => {
      switch (errorType) {
        case 'negative':
          return createErrorObject(headerName, rowNumber, errorMessages.positiveError);
        case 'percentLargerThan100':
          return createErrorObject(headerName, rowNumber, errorMessages.percentDiscountLargerThan100);
        case 'flatLargerThanPrice':
          return createErrorObject(headerName, rowNumber, errorMessages.flatDiscountGreaterThanPrice);
      }
    }
  },
  ['discount type']: {
    inputName: 'discountType',
    validate: (discountType, _ref4) => {
      let {
        rowObject
      } = _ref4;
      const excludedDiscountTypes = ['flat'];
      const isRelateToBundle = options.UPLOAD_BUNDLES_BULK && rowObject['bundleName'];
      const normalizedDiscountType = discountType === null || discountType === void 0 ? void 0 : discountType.toLowerCase();
      const valid = !normalizedDiscountType || discountTypeOptions.includes(normalizedDiscountType) && (!isRelateToBundle || !excludedDiscountTypes.includes(normalizedDiscountType));
      return {
        valid,
        errorType: valid ? undefined : isRelateToBundle ? 'flatNotAllowed' : 'invalidValue'
      };
    },
    validateError: (headerName, rowNumber, errorType) => {
      const excludeValues = errorType === 'flatNotAllowed' ? ['flat'] : [];
      return createErrorObject(headerName, rowNumber, errorMessages.getDiscountTypeError({
        excludeValues
      }));
    }
  }
}, options.UPLOAD_BUNDLES_BULK ? {
  ['bundle name']: _objectSpread({
    inputName: 'bundleName',
    requiredWhen: rowObject => !!['bundlePricing', 'bundleBillingOccurrence', 'bundleDescription'].find(key => rowObject[key]),
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.bundleNameIsRequired)
  }, getServiceNameValidations({
    isBundle: true
  })),
  ['bundle description']: {
    inputName: 'bundleDescription',
    required: false
  },
  ['bundle billing occurrence']: _objectSpread({
    inputName: 'bundleBillingOccurrence',
    requiredWhen: _ref5 => {
      let {
        bundleName
      } = _ref5;
      return !!bundleName;
    },
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.bundleBillingOccurrenceIsRequired)
  }, getBillingOccurrenceValidations({
    excludeValues: [],
    isOptional: true
  })),
  ['bundle pricing']: _objectSpread({
    inputName: 'bundlePricing',
    requiredWhen: _ref6 => {
      let {
        bundleName
      } = _ref6;
      return !!bundleName;
    },
    requiredError: (headerName, rowNumber) => createErrorObject(headerName, rowNumber, errorMessages.bundlePricingIsRequired)
  }, getPricingValidations({
    excludeValues: ['range'],
    isOptional: true
  }))
} : {});
export function runBundleValidation(data) {
  let existingServiceTemplates = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!data.length) return [];
  const errors = [];
  const bundleServices = {};
  const bundlesColumnDataToValidate = data.map(row => {
    if (existingServiceTemplates.some(s => s.name === row.bundleName)) {
      errors.push(createErrorObject('bundle name', row.lineNumber, errorMessages.nonUniqueBundleNameError));
    }
    if (!bundleServices[row.bundleName]) {
      bundleServices[row.bundleName] = {
        services: new Set(),
        lineNumber: row.lineNumber
      };
    }
    bundleServices[row.bundleName].services.add(row.serviceName);
    const validationColumns = bundleColumnsToValidate.reduce((acc, column) => {
      var _row$column;
      acc[column] = ((_row$column = row[column]) === null || _row$column === void 0 ? void 0 : _row$column.toLowerCase()) || '';
      return acc;
    }, {});
    return _objectSpread({
      bundleName: row.bundleName,
      lineNumber: row.lineNumber
    }, validationColumns);
  });
  const bundleServicesSizeErrors = validateBundleServicesSize(bundleServices);
  const bundleColumnsConsistencyErrors = validateBundleColumnsConsistency(bundlesColumnDataToValidate);
  return [...errors, ...bundleServicesSizeErrors, ...bundleColumnsConsistencyErrors];
}
function runServicesValidation(data, existingServiceTemplates) {
  if (!data.length) return [];
  const errors = [];
  const nonUniqueServicesByName = xor(data, uniqBy(existingServiceTemplates.map(s => ({
    lineNumber: -1,
    serviceName: s.name
  })).concat(data), 'serviceName')).filter(s => s.lineNumber !== -1);
  if (nonUniqueServicesByName.length) {
    nonUniqueServicesByName.forEach(rowObject => errors.push(createErrorObject('service name', rowObject.lineNumber, errorMessages.nonUniqueServiceNameError)));
  }
  return errors;
}
const getServiceBilling = function getServiceBilling(row) {
  let billingField = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'billingOccurrence';
  const isAutomaticBilling = isBoolTrue(row.automaticBilling);
  const normalizedRecurrencePeriod = row[billingField].toLowerCase();
  const isRecurring = normalizedRecurrencePeriod !== 'one time';
  const billingTrigger = isAutomaticBilling ? isRecurring ? BillingTriggerType.Ongoing : BillingTriggerType.OnApproval : isRecurring ? BillingTriggerType.RepeatableManual : BillingTriggerType.OneTimeManual;
  switch (billingTrigger) {
    case BillingTriggerType.OnApproval:
      return serviceTemplateModel.makeBillingOnApproval();
    case BillingTriggerType.OneTimeManual:
      return serviceTemplateModel.makeBillingOneTimeManual();
    case BillingTriggerType.Ongoing:
      return serviceTemplateModel.makeBillingOngoing(normalizedRecurrencePeriod);
    case BillingTriggerType.RepeatableManual:
      return serviceTemplateModel.makeBillingRepeatableManual(normalizedRecurrencePeriod);
  }
};
const createServiceTemplate = function createServiceTemplate(row) {
  let {
    id,
    ignoreServiceDiscount
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    id: null,
    ignoreServiceDiscount: false
  };
  return serviceTemplateModel.makeServiceTemplate({
    id,
    name: row.serviceName,
    description: convertStrToRichText(row.serviceDescription),
    cost: getServiceCost(row, ignoreServiceDiscount),
    billing: getServiceBilling(row)
  });
};
function createBundles(parsedCsv, existingServiceTemplates) {
  const createdBundlesAndServices = [];
  const bundles = parsedCsv.reduce((acc, row) => {
    var _existingService$id;
    const bundleName = row.bundleName;
    if (!acc[bundleName]) {
      acc[bundleName] = {
        name: bundleName,
        description: row.bundleDescription,
        billing: getServiceBilling(row, 'bundleBillingOccurrence'),
        bundle: {
          pricingType: getBundlePricingType(row),
          services: []
        },
        accumulatedCost: new Decimal(0),
        row
      };
    }
    const bundle = acc[bundleName];
    const existingService = existingServiceTemplates.find(s => s.name === row.serviceName);
    const id = (_existingService$id = existingService === null || existingService === void 0 ? void 0 : existingService.id) !== null && _existingService$id !== void 0 ? _existingService$id : null;
    const currentService = createServiceTemplate(row, {
      id,
      ignoreServiceDiscount: true
    });
    if (!existingService) {
      createdBundlesAndServices.push(currentService);
    }
    const serviceCost = currentService.cost.pricingType === ServiceCostType.Range ? currentService.cost.maxPrice : currentService.cost.price;
    bundle.bundle.services.push(_objectSpread(_objectSpread({}, currentService), {}, {
      rate: serviceCost
    }));
    bundle.accumulatedCost = bundle.accumulatedCost.add(serviceCost);
    return acc;
  }, {});
  Object.values(bundles).forEach(_ref7 => {
    let {
        accumulatedCost,
        row
      } = _ref7,
      bundle = _objectWithoutProperties(_ref7, _excluded);
    createdBundlesAndServices.push(serviceTemplateModel.makeServiceTemplate(_objectSpread(_objectSpread({}, bundle), {}, {
      cost: getServiceCost(_objectSpread(_objectSpread({}, row), {}, {
        pricing: row.bundlePricing,
        rate: accumulatedCost.toString()
      }))
    })));
  });
  return createdBundlesAndServices;
}
const createServices = parsedCsv => {
  return parsedCsv.map(row => createServiceTemplate(row));
};
function tryToParseError(error, or) {
  try {
    return JSON.parse(error);
  } catch (e) {
    return or;
  }
}
export const parseAndValidateServicesCsv = async function parseAndValidateServicesCsv(data, existingServiceTemplates) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  try {
    const columnConfig = getServicesCsvColumnConfig(options);
    const parsedData = await parseCsv(data, columnConfig);
    if (!parsedData.length) throw new Error('No data');
    validateCsvColumns(parsedData, columnConfig);
    const withLineNumbers = addCsvLineNumbers(parsedData);
    const {
      bundleRows,
      nonBundleRows
    } = extractBundles(withLineNumbers);
    const errors = [...withLineNumbers.reduce((acc, rowObject) => {
      return [...acc, ...validateCsvRow(rowObject, columnConfig)];
    }, []), ...runServicesValidation(nonBundleRows, existingServiceTemplates), ...(options.UPLOAD_BUNDLES_BULK ? runBundleValidation(bundleRows, existingServiceTemplates) : [])];
    if (!errors.length) {
      const items = [...createServices(nonBundleRows), ...(options.UPLOAD_BUNDLES_BULK ? createBundles(bundleRows, existingServiceTemplates) : [])];
      return {
        items,
        errors
      };
    } else {
      return {
        items: [],
        errors
      };
    }
  } catch (exn) {
    var _tryToParseError;
    return {
      items: [],
      errors: [{
        message: '' + exn,
        rowNumber: (exn === null || exn === void 0 ? void 0 : exn.message) != null ? (_tryToParseError = tryToParseError(exn.message, 0)) === null || _tryToParseError === void 0 ? void 0 : _tryToParseError.lineNumber : 0
      }]
    };
  }
};